import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Flex, Form, Upload } from 'antd';
import { useTranslation } from 'react-i18next';

import FormItem from '@/components/form/FormItem';
import {
  getUploadFileList,
  normUploadFileList,
} from '@/components/form/shared/uploadFieldValue';

import type { FormInstance } from 'antd/es/form';
import type { UploadProps } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/lib/upload';
import type React from 'react';
import type { ReactNode } from 'react';

type ContractFileUploadProps = {
  form: FormInstance;
  file: string | FormData | undefined;
  setFile: (file: string | FormData | undefined) => void;
  /** Form field name (default `contract`). */
  fieldName?: string;
  label?: string;
  /** Existing file URL for download link (defaults to `file` when string) */
  existingContractUrl?: string;
  /** When true, show download/delete row for existing file instead of upload */
  showExistingFile?: boolean;
  onFileChange?: () => void;
  deleteButtonDanger?: boolean;
  /** Optional validation; return false to reject the file. */
  beforeUpload?: (file: RcFile) => boolean;
  accept?: string;
  downloadLabel?: string;
  chooseFileLabel?: string;
  help?: ReactNode;
  dataTestId?: string;
};

export function ContractFileUpload({
  form,
  file,
  setFile,
  fieldName = 'contract',
  label,
  existingContractUrl,
  showExistingFile,
  onFileChange,
  deleteButtonDanger = false,
  beforeUpload,
  accept,
  downloadLabel,
  chooseFileLabel,
  help,
  dataTestId,
}: ContractFileUploadProps) {
  const { t } = useTranslation();
  const { componentDisabled } = ConfigProvider.useConfig();
  const fieldLabel = label ?? t('COMPANY_AGREEMENT_CONTRACT');
  const downloadUrl =
    existingContractUrl ?? (typeof file === 'string' ? file : '');
  const fieldValue: unknown = Form.useWatch(fieldName, form);
  const fileList = getUploadFileList(fieldValue);
  const hasExistingFile =
    showExistingFile ?? Boolean(downloadUrl && file && fileList.length === 0);

  const handleChange: UploadProps['onChange'] = (info) => {
    const nextFile = info.fileList.at(-1);

    if (nextFile?.name) {
      setFile(nextFile.name);
      onFileChange?.();
    }
  };

  const handleBeforeUpload = (uploaded: RcFile) => {
    if (beforeUpload && !beforeUpload(uploaded)) {
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const clearField = () => {
    setFile('');
    form.setFieldsValue({ [fieldName]: null });
    onFileChange?.();
  };

  const handleFileDelete = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    clearField();
  };

  const handleRemove: UploadProps['onRemove'] = () => {
    clearField();
    return true;
  };

  if (hasExistingFile) {
    return (
      <div data-testid={dataTestId}>
        <Flex>
          <Button
            color="primary"
            variant="outlined"
            icon={<DownloadOutlined />}
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
          >
            {downloadLabel ?? t('COMPANY_AGREEMENT_DOWNLOAD_CONTRACT')}
          </Button>
          {!componentDisabled ? (
            <Button
              danger={deleteButtonDanger}
              icon={<DeleteOutlined />}
              onClick={handleFileDelete}
            />
          ) : null}
        </Flex>
      </div>
    );
  }

  return (
    <div data-testid={dataTestId}>
      <FormItem
        label={fieldLabel}
        name={fieldName}
        help={help}
        valuePropName="fileList"
        getValueFromEvent={normUploadFileList}
      >
        <Upload
          name="file"
          accept={accept}
          fileList={fileList}
          maxCount={1}
          beforeUpload={handleBeforeUpload}
          customRequest={() => void 0}
          onChange={handleChange}
          onRemove={handleRemove}
          showUploadList={{
            showPreviewIcon: false,
            showRemoveIcon: true,
            showDownloadIcon: false,
          }}
        >
          <Button type="default">{chooseFileLabel ?? t('CHOOSE_FILE')}</Button>
        </Upload>
      </FormItem>
    </div>
  );
}
